<template>
  <v-container fluid>
    <v-form ref="form" v-model="form" @submit.prevent="submit">
      <v-row>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0" v-if="!operation.is_draft">
          <v-switch
            dense
            hide-details
            v-model="serviceOptForm.metadata.arrange_by_owner"
            label="Arrange By Principal?"
            class="mt-0 pt-0"
          ></v-switch>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0" v-show="!serviceOptForm.metadata.arrange_by_owner" v-if="$root.hasAccess('can_manage_operation_amount_details')">
          <v-text-field
            label="Port Dues"
            placeholder="Enter Port Dues"
            outlined
            dense
            type="number"
            v-model="serviceOptForm.amount"
            prepend-inner-icon="mdi-currency-usd"
            :hint="hintText"
            :persistent-hint="false"
            :rules="form ? amountRules : []"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0" v-show="!serviceOptForm.metadata.arrange_by_owner">
          <v-select
            label="Supplier"
            placeholder="Select a supplier"
            outlined
            dense
            v-model="serviceOptForm.card_id"
            prepend-inner-icon="mdi-account"
            :items="suppliers"
            :rules="supplierRules"
            item-text="name"
            item-value="id"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0" v-show="!serviceOptForm.metadata.arrange_by_owner" v-if="$root.hasAccess('can_manage_operation_amount_details')">
          <v-select
            label="Tax"
            placeholder="Select Tax"
            outlined
            dense
            v-model="serviceOptForm.metadata.tax"
            prepend-inner-icon="mdi-cash-fast"
            :items="taxes"
            item-text="name"
            item-value="id"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="pb-0" v-show="!serviceOptForm.metadata.arrange_by_owner">
          <v-datetime-picker
            ref="portClearanceTime"
            label="Port Clearance Time"
            v-model="serviceOptForm.metadata.port_clearance_time"
            :textFieldProps="{dense: true, readonly: true, 'prepend-inner-icon': 'mdi-calendar', outlined: true, id: 'portClearanceTimeField'}"
          >
            <template v-slot:dateIcon>
              <v-icon>mdi-calendar</v-icon>
            </template>
            <template v-slot:timeIcon>
              <v-icon>mdi-calendar-clock</v-icon>
            </template>
          </v-datetime-picker>
        </v-col>
        <v-col cols="12" class="pb-0">
          <v-textarea
            label="Remarks"
            placeholder="Enter Remarks"
            outlined
            dense
            hide-details
            :rows="2"
            v-model="serviceOptForm.remarks"
          ></v-textarea>
        </v-col>
        <v-col cols="12" class="pb-0" v-if="$root.onlyAccountsCanAccess">
          <v-textarea
            label="Accounts Remarks"
            placeholder="Enter Accounts Remarks"
            outlined
            background-color="light-blue lighten-5"
            dense
            hide-details
            :rows="2"
            v-model="serviceOptForm.accounts_remarks"
            class="accounts-remarks-field"
          ></v-textarea>
        </v-col>
        <v-col cols="12" class="pb-0">
          <v-btn class="float-right" color="success" outlined :disabled="loading" :loading="loading"
            @click="saveDetails">Save</v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
  import rules from '../../../../utilities/validator';

  const BILL_NAME = 'Port Clearance And Dues';

  /** Hours (lower, upper], rate S$ per GT unit — see refreshCalculatedBaseline for formula. */
  const PRICE_BANDS = [
    [0, 24, 8.5],
    [24, 48, 9],
    [48, 72, 9.5],
    [72, 96, 10],
    [96, 120, 11],
  ];

  function parseStoredDate(dateValue) {
    if (dateValue == null || dateValue === '' || dateValue === 'null' || dateValue === 'undefined') {
      return null;
    }
    if (dateValue instanceof Date) {
      return !isNaN(dateValue.getTime()) ? dateValue : null;
    }
    const date = new Date(dateValue);
    return !isNaN(date.getTime()) ? date : null;
  }

  export default {
    name: 's-services-opted-port-clearance-and-dues',
    props: {
      operation: {
        type: Object,
        default: {},
      },
      isDraft: {
        type: Boolean,
        default: false,
      },
      servicesOpted: {
        type: Array,
        default: []
      }
    },
    data() {
      return {
        loading: false,
        form: false,
        rules,
        serviceOptForm: {
          service_catalog_id: null,
          card_id: null,
          amount: '0.00',
          bill_type: BILL_NAME,
          remarks: '',
          accounts_remarks: '',
          metadata: {
            arrange_by_owner: false,
            tax: '',
            port_clearance_time: null,
          }
        },
        calculated_amount: 0,
        suppliers: [],
        taxes: ['0%', '9%'],
        /** Gross tonnage from GET /vessels/:id when `operation` lacks it (e.g. after Job Details save). */
        cachedVesselGrossTonnage: null,
        /** `${operationId}-${vesselId}` — only one in-flight vessel fetch per job; cleared when operation id changes. */
        vesselFetchKey: null,
      };
    },
    methods: {
      submit() {
        return this.$refs.form.validate();
      },
      handleOperationInit() {
        const { eta, etd } = this.operation;
        this.refreshCalculatedBaseline(eta, etd);
        this.populateSuppliers();
      },
      /**
       * Port Dues formula (hint baseline): hours = |ETD − ETA|, grossTon = vessel GT / 100.
       * Amount = rate × grossTon where rate depends on hours band; if hours > 120, extra term applies.
       */
      refreshCalculatedBaseline(eta, etd, skipVesselFetch = false) {
        const etaD = eta ? new Date(eta) : null;
        const etdD = etd ? new Date(etd) : null;
        if (!etaD || !etdD || Number.isNaN(etaD.getTime()) || Number.isNaN(etdD.getTime())) {
          this.calculated_amount = 0;
          return;
        }

        const op = this.operation || {};
        const num = (v) => {
          const x = parseFloat(v);
          return Number.isFinite(x) && x > 0 ? x : null;
        };
        let gtRaw = num(op.gross_tonnage) ?? num(op.vessel?.gross_tonnage) ?? num(this.cachedVesselGrossTonnage);
        let grossTon = gtRaw != null ? gtRaw / 100 : null;

        if (grossTon == null && !skipVesselFetch && op.vessel_id && this.$api?.vessels?.show) {
          const key = `${op.id || 'new'}-${op.vessel_id}`;
          if (this.vesselFetchKey !== key) {
            this.vesselFetchKey = key;
            this.$api.vessels.show(op.vessel_id).then((res) => {
              const g = parseFloat((res.vessel || {}).gross_tonnage);
              if (Number.isFinite(g) && g > 0) this.cachedVesselGrossTonnage = g;
              const { eta: e, etd: t } = this.operation;
              this.refreshCalculatedBaseline(e, t, true);
            }).catch(() => {
              const { eta: e, etd: t } = this.operation;
              this.refreshCalculatedBaseline(e, t, true);
            });
            this.calculated_amount = 0;
            return;
          }
        }

        if (grossTon == null) {
          this.calculated_amount = 0;
          return;
        }

        const hours = Math.abs(etdD - etaD) / 36e5;
        let amount = 0;
        PRICE_BANDS.forEach(([lo, hi, rate]) => {
          if (lo < hours && hours <= hi) amount = rate * grossTon;
        });
        if (hours > 120) {
          amount = (grossTon * 11) + (((hours - 120) / 24) * grossTon * 3);
        }
        this.calculated_amount = parseFloat(amount.toFixed(2));
      },
      initServiceOptedDetails() {
        const rows = this.servicesOpted || [];
        const service = rows.find(
          (row) => row.bill_type === BILL_NAME || row.service_name === BILL_NAME
        );
        const { eta, etd } = this.operation;

        if (service) {
          this.serviceOptForm = { ...this.serviceOptForm, ...service };
          this.serviceOptForm.amount = service.amount
            ? parseFloat(service.amount).toFixed(2)
            : '0.00';

          if (service.metadata && service.metadata.arrange_by_owner !== undefined) {
            this.serviceOptForm.metadata.arrange_by_owner = service.metadata.arrange_by_owner;
          }
          const meta = service.metadata || {};
          this.serviceOptForm.metadata = {
            ...this.serviceOptForm.metadata,
            ...meta,
            port_clearance_time: parseStoredDate(meta.port_clearance_time),
          };
          if (Object.prototype.hasOwnProperty.call(this.serviceOptForm.metadata, 'port_clearance_done')) {
            delete this.serviceOptForm.metadata.port_clearance_done;
          }
        }
        this.refreshCalculatedBaseline(eta, etd);
      },
      populateSuppliers() {
        this.$api.service_catalogs.get_suppliers_list(BILL_NAME)
          .then((response) => {
            this.suppliers = response.suppliers || [];
            this.serviceOptForm.service_catalog_id = response.service_catalog_id;
            this.initServiceOptedDetails();
          }).catch((err) => {
            this.$store.dispatch('setAlert', {
              status: 'error',
              show: true,
              message: err.message,
            });
          });
      },
      serializePortClearanceTime(value) {
        if (value == null || value === '') return null;
        const d = value instanceof Date ? value : new Date(value);
        return !isNaN(d.getTime()) ? d.toISOString() : null;
      },
      saveDetails() {
        if(!this.submit()) return;
        this.loading = true;
        const payload = {
          ...this.serviceOptForm,
          operation_id: this.operation.id,
          metadata: {
            ...this.serviceOptForm.metadata,
            port_clearance_time: this.serializePortClearanceTime(
              this.serviceOptForm.metadata.port_clearance_time
            ),
          },
        };
        this.$api.operation_billed_items.save_service(payload)
          .then(() => {
            this.$store.dispatch('setAlert', {
              status: 'success',
              show: true,
              message: 'Port clearance and dues service updated successfully',
            });
          }).catch((err) => {
            this.$store.dispatch('setAlert', {
              status: 'error',
              show: true,
              message: err.message,
            });
          }).finally(() => (this.loading = false));
      },
    },
    computed: {
      hintText() {
        const calc = this.calculated_amount;
        const amt = Number(this.serviceOptForm.amount);
        if (!Number.isFinite(calc) || !Number.isFinite(amt)) {
          return "";
        }
        const centsAmt = Math.round(amt * 100);
        const centsCalc = Math.round(calc * 100);
        if (centsAmt !== centsCalc) {
          return `Port Dues amount differs from calculated amount S$${calc.toFixed(2)}`;
        }
        return "";
      },
      amountRules() {
        if (this.serviceOptForm.metadata.arrange_by_owner) {
          return [this.rules.negative];
        } else {
          if (this.isDraft || (this.operation && this.operation.is_draft)) {
            return [this.rules.required, this.rules.negative];
          } else {
            return [this.rules.negative];
          }
        }
      },
      supplierRules() {
        if (this.serviceOptForm.metadata.arrange_by_owner) {
          return [];
        }
        return [this.rules.required];
      },
    },
    watch: {
      operation: {
        handler(newVal, oldVal) {
          const idChanged = !oldVal || !newVal || newVal.id !== oldVal.id;
          if (idChanged) {
            this.cachedVesselGrossTonnage = null;
            this.vesselFetchKey = null;
            this.handleOperationInit();
          } else {
            const { eta, etd } = this.operation;
            this.refreshCalculatedBaseline(eta, etd);
          }
        },
        deep: true,
      },
      servicesOpted: {
        handler() {
          if ((this.servicesOpted || []).length && this.suppliers.length) {
            this.initServiceOptedDetails();
          }
        },
        deep: true,
      },
    },
    created() {
      this.handleOperationInit();
    },
  };
</script>
